import { EyeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { NoData } from '@/app/components/no-data.tsx';
import { OnboardImportStatusIndicator } from '@/app/components/onboarding/onboard-import-status-indicator.tsx';
import OnboardJsonLineItemDialog from '@/app/components/onboarding/onboard-json-line-item-dialog.tsx';
import { Paginator, usePagination } from '@/app/components/paginator.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { Field } from '@/primitives/fieldset.tsx';
import { Input, InputGroup } from '@/primitives/input.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { TextLink } from '@/primitives/text.tsx';
import { useFilter } from '../../hooks/use-filter.ts';
import type { OnboardImportMeetingLineItemModel } from '../../types/onboard-import.type.ts';

const DisplayTime = ({ value }: { value: OnboardImportMeetingLineItemModel }) => {
  const start = DateTime.fromISO(value.meeting?.start);
  const end = DateTime.fromISO(value.meeting?.end);
  if (value.rxVantageJsonLineItem) {
    return start.isValid && end.isValid
      ? `${formatDate(start, DateTime.TIME_SIMPLE)} - ${formatDate(end, DateTime.TIME_SIMPLE)}`
      : value.rxVantageJsonLineItem.time;
  } else {
    return `${formatDate(start, DateTime.TIME_SIMPLE, value.jsonLineItem.startTime)} - ${formatDate(end, DateTime.TIME_SIMPLE, value.jsonLineItem.endTime)}`;
  }
};

const MeetingImportTable = ({ lineItems }: { lineItems: OnboardImportMeetingLineItemModel[] }) => {
  const [lineOpen, setLineOpen] = useState(false);
  const [lineItem, setLineItem] = useState(null);
  const memoizedSortingDataAccessor = useCallback(
    (value: OnboardImportMeetingLineItemModel, col: string) => {
      switch (col) {
        case 'status':
          return _(value.onboardImportStatuses)
            .map(x => x.onboardImportStatusType.id)
            .max();
        case 'date':
          return DateTime.fromISO(value.meeting?.start).isValid
            ? DateTime.fromISO(value.meeting.start)
            : (value.rxVantageJsonLineItem?.date ?? value.jsonLineItem?.date);
        case 'time':
          return formatDate(
            value.meeting?.start,
            DateTime.TIME_SIMPLE,
            value.rxVantageJsonLineItem?.time ?? value.jsonLineItem?.startTime,
          );
        default:
          return _.get(value, col);
      }
    },
    [],
  );

  const { sort, setSort, sortData } = useSort({
    data: lineItems,
    memoizedSortingDataAccessor,
  });

  const { filter, setFilter, filteredData } = useFilter({ data: sortData });
  const paginator = usePagination({ data: filteredData });
  const { pagedData } = paginator;

  return (
    <>
      <Field className='pb-5 lg:w-1/6'>
        <InputGroup>
          <Input
            type='text'
            onInput={(event: any) => setFilter(event.target.value)}
            value={filter}
            placeholder='Filter text'
          />
          <MagnifyingGlassIcon />
        </InputGroup>
      </Field>
      <Table currentSort={sort} setCurrentSort={setSort}>
        <TableHead>
          <TableRow>
            <TableHeader sortColumn='status'>Status</TableHeader>
            <TableHeader sortColumn='rowNumber'>Row #</TableHeader>
            <TableHeader sortColumn='date'>Date</TableHeader>
            <TableHeader sortColumn='time'>Time</TableHeader>
            <TableHeader sortColumn='meeting.representativeLastName'>Representative</TableHeader>
            <TableHeader sortColumn='foodCategory'>Food Category</TableHeader>
            <TableHeader sortColumn='drugName'>Drug</TableHeader>
            <TableHeader>Notes</TableHeader>
            <TableHeader></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedData.length ? (
            pagedData.map(value => (
              <TableRow key={value.id}>
                <TableCell>
                  <OnboardImportStatusIndicator
                    status={
                      _(value.onboardImportStatuses)
                        .orderBy(x => x.onboardImportStatusType.id, 'desc')
                        .head()?.onboardImportStatusType
                    }
                  />
                </TableCell>
                <TableCell>{value.rowNumber}</TableCell>
                <TableCell>
                  {DateTime.fromISO(value.meeting?.start).isValid ? (
                    <TextLink href={`/meeting/${value.meeting.key}`}>
                      {formatDate(value.meeting.start, DateTime.DATE_SHORT)}
                    </TextLink>
                  ) : (
                    (value.rxVantageJsonLineItem?.date ?? value.jsonLineItem?.date)
                  )}
                </TableCell>
                <TableCell>
                  <DisplayTime value={value} />
                </TableCell>
                <TableCell>
                  {value.meeting?.representativeUserId ? (
                    <>
                      {value.meeting?.representativeFirstName}{' '}
                      {value.meeting?.representativeLastName}
                    </>
                  ) : (
                    (value.rxVantageJsonLineItem?.representative ?? value.jsonLineItem?.repEmail)
                  )}
                </TableCell>
                <TableCell>
                  {value.meeting?.foodCategory ?? value.jsonLineItem?.foodCategory}
                </TableCell>
                <TableCell>
                  {value.meeting?.drugName ??
                    value.rxVantageJsonLineItem?.products ??
                    value.jsonLineItem?.drugName}
                </TableCell>
                <TableCell>{value.onboardImportStatuses.map(x => x.notes).join(' ')}</TableCell>
                <TableCell>
                  <Button
                    variant='light'
                    onClick={() => {
                      setLineItem(value.rxVantageJsonLineItem ?? value?.jsonLineItem);
                      setLineOpen(true);
                    }}
                    title='View line item'
                    isIconOnly
                  >
                    <EyeIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9}>
                <NoData />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Paginator {...paginator} />
      <OnboardJsonLineItemDialog
        isOpen={lineOpen}
        setOpen={setLineOpen}
        jsonLineItem={lineItem}
        title={'Meeting Line Item'}
      />
    </>
  );
};

export default MeetingImportTable;
